import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import actionsArray from "../../utils/actionsArray";
import conditionsArray from "../../utils/conditionsArray";
import ActionListItem from "./ActionListItem";
import ActionForm from "./ActionForm";
import useActions from "./useActions";
import VariantBuilderSlideOutPanel from "../variants/VariantBuilderSlideOutPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { actionHasSettings } from "../../utils/actionDefinitions";
import SidePanelEditOverlay from "../shared/SidePanelEditOverlay";

const AUTOMATION_SELECT_Z_INDEX = 1060;
const AUTOMATION_PANEL_Z_INDEX = 10010;

const ActionsBuilder = (props) => {
  const { currentActionsHook } = useActions(props);
  const Actions = currentActionsHook;
  const rootRef = useRef(null);

  const [showAutomationsForm, setShowAutomationsForm] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [panelVisibility, setPanelVisibility] = useState(false);
  const [sidePanelEl, setSidePanelEl] = useState(null);

  const cta = props.cta;
  const variant = props.variant;
  const website = props.website;
  const broadcast = props.broadcast;
  const element = props.element;

  let actions = [];

  if (element && props.contactable_type == "Element") {
    actions = Object.values(variant.actions).filter(
      (action) => action.element_id == element.id && action.contactable_type == props.contactable_type && action.toBeDeleted !== true
    );
  } else if (broadcast) {
    actions = Object.values(broadcast.actions).filter((action) => action.contactable_id == props.contactable_id);
  } else if (variant) {
    actions = Object.values(variant.actions).filter((action) => action.contactable_id == props.contactable_id);
  }

  const [existingActions, setExistingActions] = useState(
    Object.values(actions).filter(
      (action) =>
        action.contactable_type == props.contactable_type &&
        action.toBeDeleted !== true
    )
  );

  useEffect(() => {
    const panel = rootRef.current?.closest(".cf-actions-side-panel");
    setSidePanelEl(panel || null);
  }, []);

  useEffect(() => {
    const handleOpenAutomationEdit = (event) => {
      const actionId = event?.detail?.action_id;
      if (actionId == null) return;
      setEditingActionId(actionId);
      setPanelVisibility(true);
    };

    const handleBuilderUpdate = (event) => {
      const updates = event?.detail?.updates || [];
      const typeChange = updates.find(
        (update) =>
          update.object_type === "actions" &&
          update.setting_name === "[action_type]" &&
          update.object_id != null
      );
      if (typeChange && actionHasSettings(actionsList, typeChange.value)) {
        setEditingActionId(typeChange.object_id);
        setPanelVisibility(true);
      }
    };

    document.addEventListener("openAutomationEdit", handleOpenAutomationEdit);
    document.addEventListener("updateBuilder", handleBuilderUpdate);
    return () => {
      document.removeEventListener(
        "openAutomationEdit",
        handleOpenAutomationEdit
      );
      document.removeEventListener("updateBuilder", handleBuilderUpdate);
    };
  }, []);

  const actionsToUse =
    props.builder == false
      ? existingActions
      : Object.values(actions).filter((action) => {
          if (element && props.contactable_type === "Element") {
            return (
              action.element_id == element.id &&
              action.contactable_type == props.contactable_type &&
              action.toBeDeleted !== true
            );
          }
          return (
            action.contactable_type == props.contactable_type &&
            action.contactable_id == props.contactable_id &&
            action.toBeDeleted !== true
          );
        });

  const actionsList = actionsArray(
    website,
    website.integrations,
    existingActions
  );

  const conditionsList = conditionsArray(
    variant,
    cta,
    website,
    props.contactable_id,
    props.contactable_type
  );

  const editingAction =
    editingActionId == null
      ? null
      : actionsToUse.find((action) => action.id == editingActionId) || null;

  const openAutomationEdit = (action) => {
    if (!actionHasSettings(actionsList, action.action_type)) return;

    setEditingActionId(action.id);
    setPanelVisibility(true);
  };

  const closeAutomationEdit = () => {
    setEditingActionId(null);
    setPanelVisibility(false);
  };

  const closeSidePanel = () => {
    const closeBtn = sidePanelEl?.querySelector(".modal-header .close");
    if (closeBtn) {
      closeBtn.click();
    }
    setEditingActionId(null);
  };

  const handleActionUpdate = (data) => {
    if (props.builder == false) {
      setExistingActions((items) => {
        const updated = [...items];
        const targetIndex = updated.findIndex((c) => c.id == data.id);
        if (targetIndex === -1) return [...updated, data];
        updated[targetIndex] = data;
        return updated;
      });
    }
  };

  const automationEditPanel = editingAction && (
    <SidePanelEditOverlay
      darkMode={props.dark_mode}
      title={editingAction.action_type}
      subtitle="Configure this automation"
      onBack={closeAutomationEdit}
      onClose={closeSidePanel}
    >
      <ActionForm
        key={`panel-action-${editingActionId}`}
        {...props}
        layout="stacked"
        actionsList={actionsList}
        conditionsList={conditionsList}
        website={website}
        action={editingAction}
        menuPortalZIndex={AUTOMATION_SELECT_Z_INDEX}
        onUpdate={handleActionUpdate}
      />
    </SidePanelEditOverlay>
  );

  const listContent = (
    <React.Fragment>
      <p className="text-xl text-black dark:text-white">
        {props.title ? props.title : "Run Automations"}
      </p>

      {actionsToUse.length == 0 && (
        <div className="row hard-center p-5 text-black dark:text-white text-xl border-solid border-[1px] borderb-black dark:border-slate-800 mb-5">
          No automations added yet. Add an automation below!
        </div>
      )}

      <div
        className="automations flex flex-col gap-y-4 mb-4"
        style={{ maxWidth: "600px" }}
      >
        {actionsToUse.map((action, i) => {
          return (
            <div
              key={`action-${action.id}-${i}`}
              className="w-full min-w-0"
            >
              <ActionListItem
                {...props}
                object={action}
                actionsList={actionsList}
                website={website}
                onEdit={openAutomationEdit}
                onUpdate={handleActionUpdate}
                onDuplicate={(object) => {
                  Actions.duplicate(object, { element_id: object.element_id }, false, (data) => {
                    if (props.builder == false) {
                      setExistingActions((actions) => [...actions, data]);
                    }
                  });
                }}
                onRemove={(object_id) => {
                  if (props.builder == false) {
                    setExistingActions(
                      existingActions.filter((c) => c.id !== object_id)
                    );
                  }
                  if (editingActionId == object_id) {
                    closeAutomationEdit();
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      {showAutomationsForm ? (
        <ActionForm
          {...props}
          dropdownOnly={true}
          layout="inline"
          actionsList={actionsList}
          conditionsList={conditionsList}
          website={website}
          onCreate={(data) => {
            if (props.builder == false) {
              setExistingActions((a) => [...a, data]);
            }
            setShowAutomationsForm(false);
            openAutomationEdit(data);
          }}
        />
      ) : (
        <button
          onClick={() => setShowAutomationsForm(true)}
          className="w-full p-2 pl-0 text-left bg-transparent border-none text-xl text-blue-600 dark:text-blue-200 dark:hover:text-white"
        >
          <FontAwesomeIcon icon="plus-square" className="mr-2" /> Add Automation
        </button>
      )}

      {!sidePanelEl && (
        <VariantBuilderSlideOutPanel
          show={panelVisibility && !!editingAction}
          setShow={setPanelVisibility}
          title={editingAction ? editingAction.action_type : "Automation settings"}
          description="Configure this automation"
          object_id={editingAction ? editingAction.id : "new"}
          element_id={element ? element.id : null}
          builder={props.builder}
          device={props.device}
          zIndex={AUTOMATION_PANEL_Z_INDEX}
          overlayZIndex={AUTOMATION_PANEL_Z_INDEX - 1}
          width={400}
          portalToBody={true}
          showOverlay={true}
          noBorderRadius={true}
          hideFooter={true}
        >
          {editingAction && (
            <ActionForm
              key={`panel-action-${editingActionId}`}
              {...props}
              layout="stacked"
              actionsList={actionsList}
              conditionsList={conditionsList}
              website={website}
              action={editingAction}
              menuPortalZIndex={AUTOMATION_PANEL_Z_INDEX + 1}
              onUpdate={handleActionUpdate}
            />
          )}
        </VariantBuilderSlideOutPanel>
      )}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div ref={rootRef} className="col-xs-12 some-vertical-space">
        {listContent}
      </div>
      {sidePanelEl &&
        editingAction &&
        createPortal(automationEditPanel, sidePanelEl)}
    </React.Fragment>
  );
};

export default ActionsBuilder;
